import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// Format price in HUF (Hungarian Forint)
export function formatPrice(price) {
    if (typeof price !== 'number') return '0 Ft';
    return new Intl.NumberFormat('hu-HU', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(price) + ' Ft';
}

// Format price with currency symbol
export function formatCurrency(price, currency = 'HUF') {
    if (typeof price !== 'number') return '0';
    return new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(price);
}

// Calculate discount percentage
export function calculateDiscount(originalPrice, currentPrice) {
    if (!originalPrice || originalPrice <= currentPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
}

// Truncate text
export function truncateText(text, maxLength = 100) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
}

// Generate slug from text
export function slugify(text) {
    return text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
}

// Get category display name
export function getCategoryName(category) {
    const names = {
        gaming_pc: 'Gaming PCs',
        office_pc: 'Office PCs',
        workstation: 'Workstations',
        component: 'Components',
        cpu: 'Processors',
        gpu: 'Graphics Cards',
        motherboard: 'Motherboards',
        ram: 'Memory',
        storage: 'Storage',
        psu: 'Power Supplies',
        case: 'Cases',
        cooling: 'Cooling',
    };
    return names[category] || category;
}

// Get condition badge color
export function getConditionColor(condition) {
    return condition === 'new' ? 'bg-cyan-500' : 'bg-amber-500';
}

// Format date
export function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

// Get order status color
export function getOrderStatusColor(status) {
    const colors = {
        pending: 'text-yellow-500',
        confirmed: 'text-blue-500',
        processing: 'text-cyan-500',
        shipped: 'text-purple-500',
        delivered: 'text-green-500',
        cancelled: 'text-red-500',
    };
    return colors[status] || 'text-muted-foreground';
}

// Get payment status color
export function getPaymentStatusColor(status) {
    const colors = {
        pending: 'text-yellow-500',
        paid: 'text-green-500',
        failed: 'text-red-500',
    };
    return colors[status] || 'text-muted-foreground';
}

// Stock status configuration
export const STOCK_STATUSES = {
    raktaron: { label: 'Rakt\u00e1ron', color: 'text-green-500', dotColor: 'bg-green-500', delivery: 'Sz\u00e1ll\u00edt\u00e1s: 1\u20133 munkanap', desc: 'Azonnal el\u00e9rhet\u0151' },
    beszallitoi_raktaron: { label: 'Besz\u00e1ll\u00edt\u00f3i rakt\u00e1ron', color: 'text-cyan-500', dotColor: 'bg-cyan-500', delivery: 'Sz\u00e1ll\u00edt\u00e1s: 3\u20135 munkanap', desc: 'K\u00fcls\u0151 rakt\u00e1ron' },
    rendelheto: { label: 'Rendelhet\u0151', color: 'text-amber-500', dotColor: 'bg-amber-500', delivery: 'Sz\u00e1ll\u00edt\u00e1s: 5-7 munkanap', desc: 'Rendelhet\u0151' },
    nincs_keszleten: { label: 'Nincs k\u00e9szleten', color: 'text-red-500', dotColor: 'bg-red-500', delivery: '', desc: '' },
};

export function getStockStatus(product) {
    if (product.stock_status && STOCK_STATUSES[product.stock_status]) {
        return STOCK_STATUSES[product.stock_status];
    }
    return product.stock > 0 ? STOCK_STATUSES.raktaron : STOCK_STATUSES.nincs_keszleten;
}

export function isProductAvailable(product) {
    const status = product.stock_status || (product.stock > 0 ? 'raktaron' : 'nincs_keszleten');
    return status === 'raktaron' || status === 'beszallitoi_raktaron' || status === 'rendelheto';
}

